import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingCart, User, Menu, Store, LogOut, ChevronDown, Store as StoreIcon, ArrowRightLeft, ShieldCheck, Package, UserCircle, Bell, LayoutGrid, Wrench, Briefcase, Check, Megaphone, QrCode } from "lucide-react";
import { MenuShopAvailability, ShareMyProfileSheet, AdsMenuCounts } from "@/components/MenuExtras";
import { useLocationInbox, MenuLocationRows } from "@/components/MenuLocations";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import AuthDialog from "@/components/AuthDialog";
import CartSheet from "@/components/CartSheet";
import WishlistSheet from "@/components/WishlistSheet";
import PincodeSelector from "@/components/PincodeSelector";
import VendorApplyDialog from "@/components/VendorApplyDialog";
import { toast } from "sonner";
import { fetchCategoryCounts } from "@/lib/api";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import VoiceMic from "@/components/VoiceMic";

const CATEGORY_LINKS = [
  { id: "products", label: "cat.products", icon: Package },
  { id: "services", label: "cat.services", icon: Wrench },
  { id: "hiring", label: "cat.hiring", icon: Briefcase },
  { id: "shops", label: "cat.shops", icon: Store },
];

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { pincode, radiusKm, cartCount, wishlist, user, logout, isVendor, isAdmin, mode, setMode, unreadCount } = useApp();
  const [q, setQ] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [vendorApplyOpen, setVendorApplyOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  useEffect(() => {
    const open = () => setCartOpen(true);
    const openAuth = () => setAuthOpen(true);
    window.addEventListener("vb:open-cart", open);
    window.addEventListener("vb:open-auth", openAuth);
    return () => { window.removeEventListener("vb:open-cart", open); window.removeEventListener("vb:open-auth", openAuth); };
  }, []);
  const [catCounts, setCatCounts] = useState(null);
  const activeCat = location.pathname.startsWith("/category/") ? location.pathname.split("/")[2] : null;
  const [catSeen, setCatSeen] = useState(() => { try { return JSON.parse(localStorage.getItem("vb.catSeen") || "{}"); } catch { return {}; } });
  const hasNew = (id) => !!catCounts?.latest_new?.[id] && (!catSeen[id] || catSeen[id] < catCounts.latest_new[id]);
  const anyNew = CATEGORY_LINKS.some(({ id }) => hasNew(id));
  useEffect(() => {
    fetchCategoryCounts({ pincode, radius_km: radiusKm ?? undefined }).then(setCatCounts).catch(() => {});
  }, [pincode, radiusKm]);
  useEffect(() => {
    if (!activeCat) return;
    setCatSeen((prev) => {
      const next = { ...prev, [activeCat]: new Date().toISOString() };
      localStorage.setItem("vb.catSeen", JSON.stringify(next));
      return next;
    });
  }, [activeCat]);
  const loadCatCounts = (open) => {
    if (!open) return;
    fetchCategoryCounts({ pincode, radius_km: radiusKm ?? undefined }).then(setCatCounts).catch(() => setCatCounts(null));
  };
  const [wishOpen, setWishOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locItems, dropLoc] = useLocationInbox(user?.id);
  const path = location.pathname;
  const toolbagOn = path === "/vendor" && new URLSearchParams(location.search).get("toolbag") === "1";
  const isAt = (p) => (p === "/vendor" ? path === "/vendor" && !toolbagOn : p === "toolbag" ? toolbagOn : path === p || path.startsWith(p + "/"));
  const MI = (p) => ({ className: "cursor-pointer vb-menu-item", "data-active": isAt(p), "aria-current": isAt(p) ? "page" : undefined });
  const MB = (p) => ({ "data-active": isAt(p), "aria-current": isAt(p) ? "page" : undefined });

  const runSearch = (text) => {
    const params = new URLSearchParams();
    if (text) params.set("q", text);
    if (pincode) params.set("pincode", pincode);
    navigate(`/search?${params.toString()}`);
    setMobileOpen(false);
  };
  const submitSearch = (e) => { e.preventDefault(); runSearch(q); };
  // Voice: final speech result fills the box (still editable) and runs the search once.
  const voiceSearch = (txt) => { const t = txt.replace(/[।.!?,]+$/u, "").trim(); if (!t) return; setQ(t); runSearch(t); };

  const handleLogout = async () => {
    setMobileOpen(false);
    navigate("/"); // leave guarded pages (e.g. /admin) before the session clears, so no "Admin only" toast
    await logout();
    toast.success("Logged out");
  };

  const openApply = () => {
    if (!user) { setAuthOpen(true); toast.info("Login first to apply as vendor"); return; }
    setVendorApplyOpen(true);
  };

  const toggleMode = () => {
    if (!isVendor) return;
    const next = mode === "vendor" ? "customer" : "vendor";
    setMode(next);
    toast.success(`Switched to ${next === "vendor" ? "Vendor" : "Customer"} mode`);
    navigate(next === "vendor" ? "/vendor" : "/");
  };

  const initials = user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70" data-testid="app-header">
      {/* Mode indicator strip when vendor mode active */}
      {user && isVendor && mode === "vendor" && (
        <div className="bg-amber-500 text-slate-900 text-xs font-semibold text-center py-1.5" data-testid="vendor-mode-strip">
          <span className="inline-flex items-center gap-1.5"><StoreIcon className="w-3.5 h-3.5" /> Vendor Mode — you are managing your shop</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4 h-16 lg:h-20">
          <Link to="/" className="flex items-center group flex-shrink-0" data-testid="logo-link">
            <div className="leading-tight">
              <div className="font-display text-2xl lg:text-3xl font-bold text-black leading-none tracking-tight" data-testid="logo-wordmark">Ver<span className="text-emerald-700">B</span></div>
              <div className="text-[10px] lg:text-[11px] font-semibold tracking-[0.16em] text-black mt-0.5 whitespace-nowrap" data-testid="logo-tagline">Vertual <span className="text-emerald-700">Bazaar</span></div>
            </div>
          </Link>

          {!isHome && (
            <form onSubmit={submitSearch} className="hidden lg:flex flex-1 max-w-2xl items-center gap-2 bg-white border border-slate-200 rounded-full shadow-sm px-2 py-1.5" data-testid="header-search-form">
              <PincodeSelector variant="inline" />
              <div className="w-px h-6 bg-slate-200" />
              <div className="flex items-center flex-1 gap-2 px-2">
                <Search className="w-4 h-4 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400" data-testid="header-search-input" />
                <VoiceMic onText={voiceSearch} showUnsupported unsupportedMsg="Voice search isn't available in this browser — please type and tap Search." testid="header-search-mic" hint />
              </div>
              <button type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold transition-colors" data-testid="header-search-submit">Search</button>
            </form>
          )}

          <div className="header-actions flex items-center gap-0.5 sm:gap-2 min-w-0" data-testid="header-actions">
            <LanguageSelector />
            <DropdownMenu onOpenChange={loadCatCounts}>
              <DropdownMenuTrigger asChild>
                <button className="vb-nav-icon relative p-2 sm:p-2.5 rounded-full" data-active={!!activeCat && activeCat !== "hiring"} aria-label="Categories" data-testid="category-menu-btn">
                  <LayoutGrid className="w-5 h-5" />
                  {anyNew && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-white" data-testid="category-menu-new-dot" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" data-testid="category-menu">
                {CATEGORY_LINKS.map(({ id, label, icon: Icon }) => {
                  const active = activeCat === id;
                  return (
                    <DropdownMenuItem key={id} onClick={() => navigate(`/category/${id}`)} className="cursor-pointer vb-menu-item" aria-current={active ? "page" : undefined} data-testid={`category-menu-${id}`} data-active={active}>
                      <Icon className="w-4 h-4 mr-2 text-emerald-700" />
                      <span className="flex-1 flex items-center gap-1.5">{t(label)}{hasNew(id) && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" title="New today" data-testid={`category-new-dot-${id}`} />}</span>
                      {active && <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-700" data-testid={`category-menu-active-${id}`} />}
                      <span className="min-w-[1.5rem] text-center text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-600" data-testid={`category-count-${id}`}>{catCounts ? catCounts[id] : "…"}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/category/hiring" aria-label="Jobs" title="Jobs" className="vb-nav-icon relative p-2 sm:p-2.5 rounded-full" data-active={path === "/category/hiring"} aria-current={path === "/category/hiring" ? "page" : undefined} data-testid="header-jobs-link"><Briefcase className="w-5 h-5" /></Link>
            {user && (
              <button onClick={() => navigate("/notifications")} className="vb-nav-icon relative p-2 sm:p-2.5 rounded-full" data-active={isAt("/notifications")} aria-current={isAt("/notifications") ? "page" : undefined} aria-label="Notifications" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center" data-testid="notifications-unread-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
              </button>
            )}
            <button onClick={() => setWishOpen(true)} className="vb-nav-icon relative p-2 sm:p-2.5 rounded-full" aria-label="Wishlist" data-testid="wishlist-btn">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="wishlist-count">{wishlist.length}</span>}
            </button>

            {user ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button className="relative hidden sm:flex items-center gap-2 pl-2 pr-3 py-1.5 border border-slate-200 rounded-full hover:border-emerald-500 transition-colors" data-testid="user-menu-btn">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-semibold text-xs" data-testid="user-avatar">{initials}</div>
                    <span className="text-sm font-semibold text-slate-800 max-w-[100px] truncate">{user.name.split(" ")[0]}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold" data-testid="menu-user-name">{user.name}</div>
                    <div className="text-xs text-slate-500 font-normal" data-testid="menu-user-email">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} {...MI("/profile")} data-testid="my-profile-menu">
                    <UserCircle className="w-4 h-4 mr-2" /> {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/notifications")} {...MI("/notifications")} data-testid="notifications-menu">
                    <Bell className="w-4 h-4 mr-2" /> {t("nav.notifications")}
                  </DropdownMenuItem>
                  {isVendor && (
                    <DropdownMenuItem onClick={toggleMode} className="cursor-pointer vb-menu-item" data-testid="mode-toggle-menu">
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      {mode === "vendor" ? t("nav.toCustomer") : t("nav.toVendor")}
                    </DropdownMenuItem>
                  )}
                  {isVendor && (
                    <DropdownMenuItem onClick={() => navigate("/vendor")} {...MI("/vendor")} data-testid="vendor-dashboard-menu">
                      <StoreIcon className="w-4 h-4 mr-2" /> {t("nav.vendorDashboard")}
                    </DropdownMenuItem>
                  )}
                  {isVendor && (
                    <DropdownMenuItem onClick={() => navigate("/vendor?toolbag=1")} {...MI("toolbag")} data-testid="vendor-toolbag-menu">
                      <Briefcase className="w-4 h-4 mr-2" /> <span className="flex-1">{t("nav.toolbag")}</span>
                    </DropdownMenuItem>
                  )}
                  {isVendor && (
                    <DropdownMenuItem onClick={() => navigate("/vendor?tab=ads")} className="cursor-pointer vb-menu-item" data-testid="vendor-ads-menu">
                      <Megaphone className="w-4 h-4 mr-2" /> Advertisement <AdsMenuCounts />
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setShareOpen(true)} className="cursor-pointer vb-menu-item" data-testid="share-profile-menu">
                    <QrCode className="w-4 h-4 mr-2" /> Share My Profile
                  </DropdownMenuItem>
                  {isVendor && <div className="px-1 py-1" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}><MenuShopAvailability /></div>}
                  {!isVendor && (
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer vb-menu-item" data-testid="become-vendor-menu">
                      <StoreIcon className="w-4 h-4 mr-2" /> Start selling on VerB
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} {...MI("/admin")} data-testid="admin-menu">
                      <ShieldCheck className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-menu">
                    <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={() => setAuthOpen(true)} className="hidden sm:flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-4 py-2 text-sm font-semibold transition-colors ml-1" data-testid="login-btn">
                <User className="w-4 h-4" /> {t("nav.login")}
              </button>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="vb-nav-icon relative lg:hidden p-2 rounded-full" aria-label="Menu" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                  {locItems.length > 0 && <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-emerald-600 ring-2 ring-white" aria-label="Shared location active" data-testid="menu-location-dot" />}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[360px] max-h-[100dvh] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]" data-testid="mobile-menu-sheet">
                <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
                <MenuLocationRows items={locItems} onGone={dropLoc} onOpened={() => setMobileOpen(false)} />
                <form onSubmit={submitSearch} className="mt-6 space-y-3">
                  <PincodeSelector variant="block" />
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="w-full bg-transparent outline-none text-sm" data-testid="mobile-search-input" />
                    <VoiceMic onText={voiceSearch} showUnsupported unsupportedMsg="Voice search isn't available in this browser — please type and tap Search." testid="mobile-search-mic" hint />
                  </div>
                  <button type="submit" className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-3 text-sm font-semibold" data-testid="mobile-search-submit">Search</button>
                </form>
                <div className="mt-6 space-y-2">
                  {user ? (
                    <>
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                        <div className="text-sm font-semibold text-emerald-900">{user.name}</div>
                        <div className="text-xs text-emerald-700">{user.email}</div>
                      </div>
                      <button onClick={() => { navigate("/profile"); setMobileOpen(false); }} {...MB("/profile")} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-my-profile">
                        <UserCircle className="w-4 h-4" /> {t("nav.profile")}
                      </button>
                      {isVendor && (
                        <button onClick={() => { toggleMode(); setMobileOpen(false); }} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-mode-toggle">
                          <ArrowRightLeft className="w-4 h-4" /> {mode === "vendor" ? "Switch to Customer" : "Switch to Vendor"}
                        </button>
                      )}
                      {isVendor && (
                        <button onClick={() => { navigate("/vendor"); setMobileOpen(false); }} {...MB("/vendor")} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-vendor-dashboard">
                          <StoreIcon className="w-4 h-4" /> {t("nav.vendorDashboard")}
                        </button>
                      )}
                      {isVendor && (
                        <button onClick={() => { navigate("/vendor?toolbag=1"); setMobileOpen(false); }} {...MB("toolbag")} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-vendor-toolbag">
                          <Briefcase className="w-4 h-4" /> <span className="flex-1">{t("nav.toolbag")}</span>
                        </button>
                      )}
                      {isVendor && (
                        <button onClick={() => { navigate("/vendor?tab=ads"); setMobileOpen(false); }} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-vendor-ads">
                          <Megaphone className="w-4 h-4 flex-shrink-0" /> Advertisement <AdsMenuCounts />
                        </button>
                      )}
                      <button onClick={() => { setMobileOpen(false); setShareOpen(true); }} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-share-profile">
                        <QrCode className="w-4 h-4" /> Share My Profile
                      </button>
                      {isVendor && <MenuShopAvailability />}
                      {!isVendor && (
                        <button onClick={() => { navigate("/profile"); setMobileOpen(false); }} className="vb-nav w-full text-left rounded-xl border border-emerald-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-become-vendor">
                          <StoreIcon className="w-4 h-4" /> Start selling on VerB
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => { navigate("/admin"); setMobileOpen(false); }} {...MB("/admin")} className="vb-nav w-full text-left rounded-xl border border-slate-200 py-2.5 px-3 text-sm flex items-center gap-2" data-testid="mobile-admin">
                          <ShieldCheck className="w-4 h-4" /> Admin Panel
                        </button>
                      )}
                      <button onClick={handleLogout} className="w-full text-left rounded-xl border border-red-200 text-red-600 py-2.5 px-3 text-sm font-semibold flex items-center gap-2" data-testid="mobile-logout">
                        <LogOut className="w-4 h-4" /> {t("nav.logout")}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => { setAuthOpen(true); setMobileOpen(false); }} className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-3 text-sm font-semibold" data-testid="mobile-login-btn">Login / Sign up</button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {!isHome && (
          <form onSubmit={submitSearch} className="lg:hidden pb-3 flex items-center gap-1.5 min-[360px]:gap-2 bg-white border border-slate-200 rounded-full px-1.5 min-[360px]:px-2 py-1.5 shadow-sm min-w-0" data-testid="mobile-header-search-form">
            <PincodeSelector variant="inline" />
            <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
            <div className="flex items-center flex-1 gap-1.5 min-[360px]:gap-2 px-1 min-w-0">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0 max-[359px]:hidden" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="w-full bg-transparent outline-none text-sm min-w-[3rem]" data-testid="mobile-header-search" />
              <VoiceMic onText={voiceSearch} showUnsupported unsupportedMsg="Voice search isn't available in this browser — please type and tap Search." testid="mobile-header-search-mic" hint />
            </div>
            <button type="submit" className="rounded-full bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold flex-shrink-0" data-testid="mobile-header-search-btn">Go</button>
          </form>
        )}
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <ShareMyProfileSheet open={shareOpen} onClose={() => setShareOpen(false)} />
      <VendorApplyDialog open={vendorApplyOpen} onOpenChange={setVendorApplyOpen} />
      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
      <WishlistSheet open={wishOpen} onOpenChange={setWishOpen} />
    </header>
  );
}
